import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/retention";
import SafeImage from "@/components/SafeImage";

/**
 * Phase 11.5 — Generic product rail used by:
 *   - New Releases   (eventKey = new_release_click)
 *   - Best Sellers   (eventKey = best_seller_click)
 *   - Lifted Gear    (eventKey = gear_click)
 *   - Accessories    (eventKey = accessory_click)
 *
 * Pure product-focused presentation — no ecosystem copy. Hides the
 * section entirely if no products are available so the homepage never
 * shows an empty rail.
 */
export default function ProductRail({
  id, testid, eyebrow, title, blurb, products, ctaTo, ctaLabel,
  eventKey = "product_click", maxItems = 4,
}) {
  const items = (products || []).slice(0, maxItems);
  if (!items.length) return null;
  return (
    <section
      id={id}
      data-testid={testid}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12"
    >
      <header className="flex items-end justify-between gap-4 flex-wrap mb-6 fade-up">
        <div>
          <div className="eyebrow">{eyebrow}</div>
          <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-2">{title}</h2>
          {blurb ? <p className="text-lifted-dim text-sm mt-2 max-w-xl">{blurb}</p> : null}
        </div>
        {ctaTo ? (
          <Link
            to={ctaTo}
            className="btn-ghost"
            data-testid={`${testid}-view-all`}
            onClick={() => trackEvent(eventKey, { surface: testid, cta: "view_all" })}
          >
            {ctaLabel || "Shop all"} <ArrowRight className="h-3 w-3" />
          </Link>
        ) : null}
      </header>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((p) => (
          <Link
            key={p.slug}
            to={`/products/${p.slug}`}
            data-testid={`${testid}-card-${p.slug}`}
            onClick={() => trackEvent(eventKey, { slug: p.slug, surface: testid })}
            className="glass-panel overflow-hidden hover:-translate-y-1 transition-transform"
          >
            <div className="aspect-square bg-black overflow-hidden relative">
              <SafeImage
                src={(p.media && p.media[0]) || p.image || ""}
                alt={p.title}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              {p.same_day_eligible && (
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-lifted-green/90 text-black text-[10px] font-bebas tracking-[0.2em] uppercase"
                      data-testid={`${testid}-sameday-${p.slug}`}>
                  Same-day
                </span>
              )}
            </div>
            <div className="p-3">
              <div className="font-anton uppercase text-sm leading-tight">{p.title}</div>
              <div className="font-bebas tracking-[0.18em] text-xs text-lifted-green mt-1">
                ${(p.price_cents / 100).toFixed(2)}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
